import {
  addDays,
  differenceInDays,
  differenceInMilliseconds,
  differenceInMonths,
  endOfDay,
  format,
  isValid,
  parse,
  startOfToday,
  startOfTomorrow,
  isBefore,
  eachDayOfInterval,
  isWeekend,
} from "date-fns";
import {
  anyPass,
  complement,
  compose,
  curry,
  defaultTo,
  filter,
  includes,
  is,
  isEmpty,
  isNil,
  keys,
  map,
  omit,
  pipe,
  replace,
  startsWith,
  toPairs,
  trim,
  when,
  match,
} from "ramda";
import { StatusCodes } from "http-status-codes";
import { checkPermission } from "../apis/kilnBackendApis";

export const compareString = (a, b) => {
  const nameA = a?.toUpperCase(); // ignore upper and lowercase
  const nameB = b?.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  // names must be equal
  return 0;
};

export const applySort = (data, sortFn, descending) => {
  const result = data.slice();
  result.sort(sortFn);
  return descending ? result.reverse() : result;
};

export const arrayExtractor = (array) => {
  return isNil(array) ? undefined : array[0];
};

export const compareDate = (dateLeft, dateRight, formatReference) => {
  return differenceInMilliseconds(
    parse(dateLeft, formatReference, new Date()),
    parse(dateRight, formatReference, new Date()),
  );
};

export const dateFormat = {
  ddMMyyyyWithSlash: "dd/MM/yyyy",
  yyyyMMdd: "yyyyMMdd",
  yyyyMMddWithDash: "yyyy-MM-dd",
  doMMMyyyyWithSpace: "do MMM yyyy",
};

export const calculateTotalPages = (total, size) => Math.ceil(total / size);

export const isNotNilEmpty = complement(anyPass([isNil, isEmpty]));

export const isNilOrEmpty = anyPass([isNil, isEmpty]);

export const todayDateFormatted = () => {
  return format(startOfToday(), dateFormat.yyyyMMddWithDash);
};

export const tomorrowDateFormatted = () => {
  return format(startOfTomorrow(), dateFormat.yyyyMMddWithDash);
};

export const guessAndFormatATimeString = (timeString, targetFormat = "HH:mm", defaultFallback = "09:00") => {
  // assume timeString in a format like 9:00am
  if (isNilOrEmpty(timeString)) {
    return defaultFallback;
  }
  // guarantee there are no multiple whitespace, all upper case and no dot
  const wellFormedTimeString = String(timeString).replace(/  +/g, " ").toUpperCase().replaceAll(".", "");
  const amPmRegEx = /([ap])\.m\.|([ap])m|([AP])\.M\.|([AP])M/gi;
  const amPmIndex = String(wellFormedTimeString).search(amPmRegEx);
  if (amPmIndex === -1) {
    // no matching for regex
    // potentially a 24-hour string
    if (isValid(parse(wellFormedTimeString, targetFormat, new Date()))) {
      return wellFormedTimeString;
    }
    return defaultFallback;
  }
  let potential12HourTimeString = wellFormedTimeString;
  // there is matching regex
  if (wellFormedTimeString[amPmIndex - 1] !== " ") {
    // no space between numeric time and am/pm
    // we need to add the whitespace
    potential12HourTimeString =
      wellFormedTimeString.substring(0, amPmIndex) + " " + wellFormedTimeString.substring(amPmIndex);
  }
  if (isValid(parse(potential12HourTimeString, "hh:mm aa", new Date()))) {
    const parsedDate = parse(potential12HourTimeString, "hh:mm aa", new Date());
    return format(parsedDate, targetFormat);
  }

  return defaultFallback;
};

export const isIncluded = (formArray, targetString) => {
  if (isNotNilEmpty(formArray)) {
    return formArray.includes(targetString);
  }
  return false;
};

export const isNumeric = (value) => {
  return /^-?\d+$/.test(value);
};

// ["Monday"] -> "Monday", ["Monday", "Friday"] -> "Monday and Friday", ["Mon", "Tue", "Fri"] -> "Mon, Tue and Fri"
export const joinWithAnd = (items = []) => {
  if (items.length < 2) {
    return items.join("");
  }
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

// Inverse of joinWithAnd; also accepts plain comma-separated values so older saved forms still parse.
export const splitJoinedList = (value) => (value || "").split(/\s*,\s*|\s+and\s+/).filter(Boolean);

export const getLatestStartDateBasedOnEndDate = (endDate) => {
  return isNil(endDate) ? null : endDate;
};

export const getEarliestEndDateBasedOnStartDate = (startDate) => {
  return isNil(startDate) ? format(startOfToday(), "yyyy-MM-dd") : startDate;
};

const addWeekdays = (startDate, weekdaysToAdd) => {
  let currentDate = startDate;
  let weekdaysAdded = 0;

  // If starting from a weekend, move to the next Monday
  if (isWeekend(currentDate)) {
    while (isWeekend(currentDate)) {
      currentDate = addDays(currentDate, 1);
    }
  }

  while (weekdaysAdded < weekdaysToAdd) {
    currentDate = addDays(currentDate, 1);
    if (!isWeekend(currentDate)) {
      weekdaysAdded++;
    }
  }

  return currentDate;
};

export const getLatestEndDateBasedOnStartDateAndMaxDays = (startDate, maxDays) => {
  if (!startDate) {
    return null;
  }
  const parsedStartDate = parse(startDate, "yyyy-MM-dd", new Date());
  const maxEndDate = addWeekdays(parsedStartDate, maxDays - 1); // Subtract 1 day as the start date counts as a leave day
  return format(maxEndDate, "yyyy-MM-dd");
};

// Flare's format for an AU mobile: "+61" then a mobile starting with 4 and
// 8 more digits (e.g. +61421246345) — exactly 12 chars, no spaces.
const AU_MOBILE_NUMBER_REGEX = /^\+614\d{8}$/;

// Remove all whitespace so a pasted number like "+61 421 246 345" becomes the
// "+61421246345" Flare expects (Flare counts spaces toward its 12-char limit).
const removeWhitespace = (value) => (typeof value === "string" ? value.replace(/\s/g, "") : value);

export const isValidAuMobileNumber = (value) =>
  typeof value === "string" && AU_MOBILE_NUMBER_REGEX.test(removeWhitespace(value));

// NZ mobile: "+64" then a mobile starting with 2 and 7-9 more digits
// (e.g. +64211234567). Unlike AU, NZ subscriber numbers vary in length.
// NZ numbers don't go to Flare, so there is no 12-char cap here.
const NZ_MOBILE_NUMBER_REGEX = /^\+642\d{7,9}$/;

export const isValidNzMobileNumber = (value) =>
  typeof value === "string" && NZ_MOBILE_NUMBER_REGEX.test(removeWhitespace(value));

export function sanitiseFormData(data) {
  // Some of the drop-downs have object data instead of strings so we need
  // to extract them here before we send it to the backend.
  const keysToRemove = extractUnderscoredKeysFromObj(data);
  const saneData = { ...omit(keysToRemove)(data) };
  // string replace '<>' with space and trim
  const replaceSpecialChars = pipe(replaceSpecialCharsWithWhiteSpace, stripWhitespaceFromBothEnds);
  // position details
  saneData.function = data?.function?.function || undefined;
  saneData.group = data?.group?.group || undefined;
  saneData.department = data?.department?.department || undefined;
  saneData.team = data?.team?.team || undefined;
  saneData.positionTitle = data?.positionTitle || undefined;
  // IT details
  saneData.archieAnalyticIdenticalAccess = data?.archieAnalyticIdenticalAccess?.fullName || undefined;
  saneData.archieIdenticalAccess = data?.archieIdenticalAccess?.fullName || undefined;
  saneData.archieReportingManager = data?.archieReportingManager?.fullName || undefined;

  // effectiveDate fallback
  saneData.effectiveDate =
    saneData?.effectiveDate || saneData?.startDate || format(startOfToday(), dateFormat.yyyyMMddWithDash);
  Object.keys(saneData).forEach((param) => {
    if (typeof saneData?.[param] === "string") {
      saneData[param] = replaceSpecialChars(saneData[param]);
    }
  });
  if (isNotNilEmpty(data?.baseSalary)) {
    saneData.baseSalary = data.baseSalary.toString();
  }
  // Strip spaces from the mobile number so Flare receives it clean.
  saneData.phoneNumber = removeWhitespace(saneData.phoneNumber);
  // Country-based logic for superannuation and total salary
  if (
    data?.location?.country?.code === "AU" &&
    isNotNilEmpty(data?.superannuation) &&
    isNotNilEmpty(data?.totalSalary)
  ) {
    saneData.superannuation = data.superannuation.toString();
    saneData.totalSalary = data.totalSalary.toString();
  }
  return saneData;
}

/**
 * Returns T/F based on whether the date is within X-amount of days from today.
 * @param {string} dateToCheck the date this func is checking in yyyy-MM-dd
 * @param {number} duration how many days from now on
 */
export const isWithinXAmountOfDaysInTheFuture = (dateToCheck, duration) => {
  const parsedDateToCheck = parse(dateToCheck, dateFormat.yyyyMMddWithDash, new Date());
  const xAmountOfDaysFromNow = addDays(Date.now(), duration);
  return startOfToday() <= parsedDateToCheck && parsedDateToCheck <= xAmountOfDaysFromNow;
};

export const calculateProbationPeriod = (startDate, endDate) => {
  const parsedStart = new Date(parse(startDate, dateFormat.yyyyMMddWithDash, new Date()));
  const parsedEnd = new Date(parse(endDate, dateFormat.yyyyMMddWithDash, new Date()));
  const diffInMonths = differenceInMonths(parsedEnd, parsedStart);
  // Contracts of longer than 12 months have probation period of six months
  if (diffInMonths >= 12) {
    return "6";
  } else {
    const result = Math.round(diffInMonths * 0.3);
    return result && !Number.isNaN(result) ? result.toString() : "0";
  }
};

export const isLocationInPreviewMode = (location) => {
  const currentPathname = location?.pathname.toLowerCase() || "";
  return currentPathname.includes("preview");
};

export const extractUnderscoredKeysFromObj = (obj) => {
  const keyList = keys(obj);
  const startsWithUnderscore = startsWith("_");
  return filter(startsWithUnderscore, keyList);
};

/**
 * Strings in JS are represented fundamentally as sequences of UTF-16 code units
 * See here:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String#utf-16_characters_unicode_code_points_and_grapheme_clusters
 * NodeJS in this regard buries the conversion logic in C++ code,
 * https://github.com/nodejs/node/blob/5c65565108c626884c5c722bb512c7c1e5c1c809/src/string_bytes.cc#L317-L375
 * which eventually calls out to the V8 binary to handle it.
 * This method replaces each instance of certain characters by one, two, three,
 * or four escape sequences representing the UTF-8 encoding of the character
 * @param {string} str
 * @returns {string} RFC5987-compliant string
 */
const encodeRFC5987ValueChars = (str) =>
  encodeURIComponent(str)
    // The following creates the sequences %27 %28 %29 %2A (Note that
    // the valid encoding of "*" is %2A, which necessitates calling
    // toUpperCase() to properly encode). Although RFC3986 reserves "!",
    // RFC5987 does not, so we do not need to escape it.
    .replace(/['()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
    // The following are not required for percent-encoding per RFC5987,
    // so we can allow for a little better readability over the wire: |`^
    .replace(/%(7C|60|5E)/g, (str, hex) => String.fromCharCode(parseInt(hex, 16)));

/** Replaces long dash characters in a filename */
export const fixFileName = (filename) => {
  return encodeRFC5987ValueChars(filename);
};

export const stripWhitespaceFromBothEnds = when(is(String), trim);
export const replaceSpecialCharsWithWhiteSpace = when(is(String), replace(/[<>]/g, " "));

/**
 * Given a full name it returns its legalFirstName,
 * preferredFirstName, and surname in a object.
 * @param {string} fullName Full name to check.
 * @returns {object} Return object { legalFirstName, preferredFirstName, surname },
 */
export const extractFullNameValues = (fullName) => {
  let legalFirstName, preferredFirstName, surname;
  legalFirstName = preferredFirstName = surname = "";

  if (isNotNilEmpty(fullName)) {
    const fullNameList = fullName.trim().split(" ") || [];

    if (fullNameList.length === 1) {
      // Either first name or surname.
      legalFirstName = preferredFirstName = fullNameList[0];
    } else if (fullNameList.length === 2) {
      // first name + surname.
      legalFirstName = preferredFirstName = fullNameList[0];
      surname = fullNameList[1];
    } else if (fullNameList.length === 3) {
      // first name + preferred first name + surname.
      legalFirstName = fullNameList[0];
      preferredFirstName = fullNameList[1];
      surname = fullNameList[2];
    }
  }
  return {
    legalFirstName,
    preferredFirstName,
    surname,
  };
};

const toFixedDecimal = (inputNumber, decimals = 2) => {
  const result = inputNumber.toFixed(decimals);
  return result <= inputNumber ? result : (result - Math.pow(0.1, decimals)).toFixed(decimals);
};

/** Australian superannuation rate value. */
const AUSTRALIAN_SUPER_RATE_BEFORE_JULY_2025 = 0.115;
const AUSTRALIAN_SUPER_RATE_FROM_JULY_2025 = 0.12;
const getAustralianSuperRate = (startDate) => {
  if (!startDate) {
    return AUSTRALIAN_SUPER_RATE_FROM_JULY_2025;
  }
  return isBefore(new Date(startDate), new Date("2025-07-01"))
    ? AUSTRALIAN_SUPER_RATE_BEFORE_JULY_2025
    : AUSTRALIAN_SUPER_RATE_FROM_JULY_2025;
};
// Return superannuation and total salary values as numbers
// given a base salary with only two decimals,
// and without rounding if it's required.
export const calculateSuperannuationAndTotalSalary = (baseSalaryValue, startDate) => {
  let superannuationValue, totalSalaryValue;
  superannuationValue = totalSalaryValue = 0;

  if (isNotNilEmpty(baseSalaryValue)) {
    superannuationValue = toFixedDecimal(baseSalaryValue * getAustralianSuperRate(startDate));
    let decimalPart;
    if (superannuationValue.indexOf(".") > 0) {
      // Remove trailing 0's.
      decimalPart = superannuationValue.split(".")[1].replace(/0+$/, "");
      const integerPart = Math.trunc(superannuationValue);
      if (isNilOrEmpty(decimalPart)) {
        superannuationValue = integerPart;
      } else {
        superannuationValue = parseFloat(`${integerPart}.${decimalPart}`);
      }
    }
    totalSalaryValue = parseInt(baseSalaryValue, 10) + parseInt(superannuationValue, 10);
    if (decimalPart) {
      totalSalaryValue = parseFloat(`${totalSalaryValue}.${decimalPart}`);
    }
  }
  return {
    superannuationValue,
    totalSalaryValue,
  };
};

//verifyRole takes history hook, permission check form type and the form name for the error message
export const verifyRole = async (history, form, formName) => {
  const permissionResp = await checkPermission(form);
  if (permissionResp.status !== StatusCodes.OK) {
    history("/error", {
      state: {
        messages: [
          `Sorry, ${formName} access is restricted to MYOB People Leaders.`,
          "If you believe you should have access, please contact your manager or People Consultants, or raise a HelpMe ticket.",
        ],
      },
    });
    return false;
  } else {
    return true;
  }
};

export const clearFields = (formPersist) => {
  formPersist.clear();
  window.location.reload();
};

export const cancelForm = (formPersist, history) => {
  formPersist.clear();
  history("/");
};

/**
 * Syncs local storage data with current employees list using unique identifiers.
 * This prevents data mixup when direct reports change.
 *
 * Strategy: Clean localStorage by keeping only matching employees (compacted array),
 * then manually populate form fields by matching userPrincipalName to correct index.
 *
 * @param {Array} employees - Current list of employees/direct reports
 * @param {Function} setValue - React Hook Form setValue function
 * @param {String} storageKey - The storage key (e.g., "employment-forms.eoyRating")
 * @param {String} formFieldName - The form field name (e.g., "ratings" or "reviews")
 * @param {String} uniqueIdField - The field name to use as unique identifier (default: "userPrincipalName")
 * @returns {boolean} - Returns true if sync was successful, false otherwise
 */
export const syncLocalStorageWithEmployees = (
  employees,
  setValue,
  storageKey,
  formFieldName,
  uniqueIdField = "userPrincipalName",
) => {
  if (!employees || employees.length === 0) {
    return false;
  }

  const storageItem = localStorage.getItem(storageKey);
  if (!storageItem) {
    return false;
  }

  try {
    const parsedStorage = JSON.parse(storageItem);

    const storedFormData = parsedStorage[formFieldName];

    if (!storedFormData || !Array.isArray(storedFormData)) {
      return false;
    }

    // Create a map of stored data by unique identifier, keeping only first occurrence of duplicates
    const storedDataMap = new Map();
    const seenKeys = new Set();

    storedFormData.forEach((item) => {
      const uniqueId = item[uniqueIdField];
      if (uniqueId && !seenKeys.has(uniqueId)) {
        storedDataMap.set(uniqueId, item);
        seenKeys.add(uniqueId);
      }
    });

    // Additional cleanup: Remove any stored data for employees not in current list
    // This prevents stale data from persisting when employees are removed or replaced
    const currentEmployeeIds = new Set(employees.map((emp) => emp[uniqueIdField]));
    const keysToRemove = [];

    for (const [uniqueId] of storedDataMap) {
      if (!currentEmployeeIds.has(uniqueId)) {
        keysToRemove.push(uniqueId);
      }
    }

    if (keysToRemove.length > 0) {
      keysToRemove.forEach((key) => storedDataMap.delete(key));
    }

    // Build compacted array with only matching employees (no gaps)
    const syncedData = [];
    let hasAnyData = false;

    // First, clear ALL form fields to prevent any stale data from persisting
    employees.forEach((employee, index) => {
      setValue(`${formFieldName}[${index}]`, undefined, {
        shouldValidate: false,
        shouldDirty: false,
      });
    });

    // Then populate form fields by matching userPrincipalName to correct employee index
    employees.forEach((employee, index) => {
      const uniqueId = employee[uniqueIdField];
      const storedItem = storedDataMap.get(uniqueId);

      if (storedItem) {
        // Match found - add to compacted array for storage
        syncedData.push(storedItem);
        hasAnyData = true;

        // Populate form field at the correct index for this employee
        Object.keys(storedItem).forEach((key) => {
          setValue(`${formFieldName}[${index}].${key}`, storedItem[key], {
            shouldValidate: false,
            shouldDirty: false,
          });
        });
      }
      // If no stored data, the field remains undefined (already cleared above)
    });

    // Update localStorage with compacted, cleaned data (no gaps, only matching employees)
    if (hasAnyData) {
      const updatedStorage = {
        ...parsedStorage,
        [formFieldName]: syncedData,
      };

      // Completely replace the localStorage data to prevent any race conditions
      localStorage.setItem(storageKey, JSON.stringify(updatedStorage));

      return true;
    } else {
      // No matching records found, clear the storage
      localStorage.removeItem(storageKey);
      return false;
    }
  } catch (error) {
    // On error, clear to prevent data corruption
    localStorage.removeItem(storageKey);
    return false;
  }
};

/**
 *
 * @param {array} currentOfficeLocationList Current location list that are displayed in the dropdowns
 * @param {string} selectedLocation The selected office location
 */
export const getLocationList = (currentOfficeLocationList, selectedLocation) => {
  const _alteredOfficeList = currentOfficeLocationList.some((e) => e.label === selectedLocation)
    ? currentOfficeLocationList
    : [...currentOfficeLocationList, { name: selectedLocation, label: selectedLocation }];
  return _alteredOfficeList.sort((a, b) => a.label.localeCompare(b.label));
};

export const safeIncludes = curry((query, target) => includes(query, defaultTo([])(target)));

export const formTypeToApiEndpointMapping = {
  "departure": "departure",
  "onboarding": "onboarding",
  "role-update": "role-update",
  "talent-review": "talent-review",
  "third-party-contractor": "third-party-contractor",
  "eoy-rating": "eoy-rating",
};

export const formTypeToFormNameMapping = {
  "departure": "Departure",
  "onboarding": "Onboarding",
  "role-update": "Role-Update/Transfer Form",
  "talent-review": "Talent Review",
  "third-party-contractor": "Third Party Contractor",
  "eoy-rating": "EOY Rating",
};

export const stringifySearchParams = (searchParamObj = {}) => {
  const sanitize = compose(toPairs, map(defaultTo("")));
  const sortedSearchParams = new URLSearchParams([...sanitize(searchParamObj)]);
  sortedSearchParams.sort();

  return sortedSearchParams.toString();
};

export const clearFormAndScrollToTop = async (form, formPersist) => {
  await form.reset();
  // Clear all _value fields used for display in EEFormSearch components
  clearDisplayValueFields(form);
  // Clear the form persistence data from localStorage
  if (formPersist) {
    formPersist.clear();
  }
  // Setting the scroll position to the top -1 as 0 is not working on Safari & Firefox
  window.scrollTo({ top: -1, left: 0, behavior: "smooth" });
};

/**
 * Clears only user-input fields (rating and ratingJustification) from the EOY ratings array,
 * preserving all employee metadata fields (userFullName, userPrincipalName, etc.)
 * @param {Object} form - The react-hook-form instance
 * @param {Object} formPersist - The form persistence hook instance
 */
export const clearEoyRatingFormAndScrollToTop = async (form, formPersist) => {
  await clearFormAndScrollToTop(form, formPersist);
  // Refresh the page to ensure UI is cleared
  window.location.reload();
};

/**
 * Clears all _value fields that are used for display purposes in EEFormSearch components.
 * These fields are not cleared by form.reset() but are needed to clear the display values.
 * @param {Object} form - The react-hook-form instance
 */
export const clearDisplayValueFields = (form) => {
  const formValues = form.getValues();
  const fieldNames = Object.keys(formValues);

  // Find all fields that have corresponding _value fields
  fieldNames.forEach((fieldName) => {
    const underscoreFieldName = `_${fieldName}`;
    // Check if the _value field exists and clear it
    if (form.getValues(underscoreFieldName) !== undefined) {
      form.setValue(underscoreFieldName, null);
    }
  });
};
export const transformFinishDateToNumber = (finishDateString) => {
  const date = parse(finishDateString, "yyyy-MM-dd", new Date());
  if (isValid(date)) {
    return endOfDay(date).getTime();
  }
};
export const transformStartDateToNumber = (startDateString) => {
  // parse will convert string to Date with env TimeZone.
  const date = parse(startDateString, "yyyy-MM-dd", new Date());
  if (isValid(date)) {
    return date.getTime();
  }
};

//this function is used to set the value of the dropdowns
//options has to be an array of objects with a label key - ensure options is passed in correctly before calling this function
export const handleSetValue = (name, value, options = [], setValue) => {
  const _options = options?.map((option) => option.label);
  if (isNotNilEmpty(value) && _options?.includes(value)) {
    setValue(name, value);
  }
};

export const isLocationInEditOrPreviewMode = (location) => {
  const currentPathname = location?.pathname.toLowerCase() || "";
  return isNotNilEmpty(match(/edit|preview/g, currentPathname));
};

export const calculateDurationInDays = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return 0;
  }

  const parsedStartDate = parse(startDate, dateFormat.yyyyMMddWithDash, new Date());
  const parsedEndDate = parse(endDate, dateFormat.yyyyMMddWithDash, new Date());

  if (!isValid(parsedStartDate) || !isValid(parsedEndDate)) {
    return 0;
  }

  // Difference is inclusive of both start and end dates
  const totalDays = differenceInDays(parsedEndDate, parsedStartDate) + 1;

  if (totalDays <= 0) {
    return 0;
  }

  return totalDays;
};

export const calculateDurationInWeekdays = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return 0;
  }

  const parsedStartDate = parse(startDate, dateFormat.yyyyMMddWithDash, new Date());
  const parsedEndDate = parse(endDate, dateFormat.yyyyMMddWithDash, new Date());

  if (!isValid(parsedStartDate) || !isValid(parsedEndDate)) {
    return 0;
  }

  // If end date is before start date, return 0
  if (parsedEndDate < parsedStartDate) {
    return 0;
  }

  // Get all days in the interval (inclusive)
  const allDays = eachDayOfInterval({
    start: parsedStartDate,
    end: parsedEndDate,
  });

  // Count only weekdays (exclude weekends)
  const weekdays = allDays.filter((day) => !isWeekend(day));

  return weekdays.length;
};

export const findEarliestDate = (dates) => {
  const validDates = dates
    .filter((date) => date && isValid(parse(date, dateFormat.yyyyMMddWithDash, new Date())))
    .map((date) => parse(date, dateFormat.yyyyMMddWithDash, new Date()));

  if (validDates.length === 0) {
    return "";
  }

  const earliestDate = validDates.reduce((earliest, current) => (current < earliest ? current : earliest));

  return format(earliestDate, dateFormat.yyyyMMddWithDash);
};

export const findLatestDate = (dates) => {
  const validDates = dates
    .filter((date) => date && isValid(parse(date, dateFormat.yyyyMMddWithDash, new Date())))
    .map((date) => parse(date, dateFormat.yyyyMMddWithDash, new Date()));

  if (validDates.length === 0) {
    return "";
  }

  const latestDate = validDates.reduce((latest, current) => (current > latest ? current : latest));

  return format(latestDate, dateFormat.yyyyMMddWithDash);
};

const doDateRangesOverlap = (range1, range2) => {
  if (!range1.startDate || !range1.endDate || !range2.startDate || !range2.endDate) {
    return false;
  }

  const range1StartDate = parse(range1.startDate, dateFormat.yyyyMMddWithDash, new Date());
  const range1EndDate = parse(range1.endDate, dateFormat.yyyyMMddWithDash, new Date());
  const range2StartDate = parse(range2.startDate, dateFormat.yyyyMMddWithDash, new Date());
  const range2EndDate = parse(range2.endDate, dateFormat.yyyyMMddWithDash, new Date());

  if (!isValid(range1StartDate) || !isValid(range1EndDate) || !isValid(range2StartDate) || !isValid(range2EndDate)) {
    return false;
  }

  return range1StartDate <= range2EndDate && range2StartDate <= range1EndDate;
};

export const checkForOverlappingDateRanges = (dateRanges) => {
  const overlaps = [];

  for (let i = 0; i < dateRanges.length; i++) {
    for (let j = i + 1; j < dateRanges.length; j++) {
      const range1 = dateRanges[i];
      const range2 = dateRanges[j];

      if (doDateRangesOverlap(range1, range2)) {
        overlaps.push({ range1, range2 });
      }
    }
  }

  return overlaps;
};

/**
 * Merges primary data with fallback data, only adding fallback items
 * whose key doesn't already exist in the primary data.
 *
 * Use this wherever API data is supplemented with temporary hardcoded fallbacks.
 *
 * @param {Array} primaryData - Items from the API (source of truth)
 * @param {Array} fallbackData - Hardcoded temp items to fill gaps
 * @param {Function} keyFn - Extracts a unique identifier from each item (e.g. (x) => x.code)
 * @returns {Array} primaryData + any fallback items whose key wasn't already present
 */
export const mergeWithFallback = (primaryData, fallbackData, keyFn) => {
  const existingKeys = new Set(primaryData.map(keyFn));
  const missing = fallbackData.filter((item) => !existingKeys.has(keyFn(item)));
  return [...primaryData, ...missing];
};

export const convertDashDateToSlashDate = (dateString) => {
  if (!dateString || typeof dateString !== "string") {
    return "";
  }

  try {
    const parsedDate = parse(dateString, dateFormat.yyyyMMddWithDash, new Date());
    // Check if the parsed date is valid
    if (isNaN(parsedDate.getTime())) {
      return "";
    }
    return format(parsedDate, dateFormat.ddMMyyyyWithSlash);
  } catch (error) {
    return "";
  }
};
